import { setLocale, t } from '@signage-player/renderer-ui';
import { isObserverInstance, peekUrlInstanceId } from '@signage-player/device-browser';
import { navigateToPicker } from './instanceHistory';

// Cursor and the settings gear (webSettings.ts) share this idle window.
const IDLE_MS = 5000;

/**
 * Web-only chrome for the shared loader/pairing screens: footer link buttons
 * (bottom-right, matching the picker; raised above the pairing instruction
 * text), a top-left back button on the pairing screen that returns to the
 * instance picker, and a cursor that appears on mouse move / tap and hides
 * after idle (the shared CSS sets cursor:none globally for kiosks). The
 * settings gear (webSettings.ts) shows and hides on the same activity class.
 */
export function installWebChrome(): void {
  // Browser locale, set before any t() — bootstrap later sets the same value.
  setLocale((navigator.language || 'en').split('-')[0]);
  // Bootstrap writes the same value, but only after the renderer module loads
  // (seconds in dev) — fill it now so the loader never shows without it.
  const version = document.querySelector('#version');
  if (version) version.textContent = `${__APP_VERSION__}`;
  injectStyles();
  injectLinks('#loader-container');
  injectLinks('#device-id-container');
  injectObserverBadge();
  injectBackButton();
  injectPairingFireworks();
  installCursorIdle();
}

function injectStyles(): void {
  const style = document.createElement('style');
  style.textContent = `
    .web-chrome-links { position: fixed; right: 43px; bottom: 35px; display: flex; gap: 12px; z-index: 100; }
    @media screen and (max-width: 950px) {
      .web-chrome-links { right: 16px; bottom: 16px; }
    }
    /* Narrow OR short screens: fixed buttons over a scrollable card always overlap
       mid-scroll — join the pairing screen's scroll flow below the card instead. */
    @media screen and (max-width: 640px), screen and (max-height: 860px) {
      .device-id .web-chrome-links { position: static; justify-content: center; flex-wrap: wrap; margin: 0 auto 24px; }
    }
    /* Solid backgrounds so the veil grid never shows through the buttons;
       blues from the website palette (#1f75de primary, #3989eb/#5da7ff hover). */
    .web-chrome-links a, .web-chrome-links a * { color: #e8e8e8; font-size: 15px; text-decoration: none; }
    .web-chrome-links a { padding: 10px 18px; border: 1px solid #2c3a4d; border-radius: 8px; white-space: nowrap;
      background: #0d1626;
      font-family: 'Poppins', system-ui, sans-serif;
      transition: border-color 0.12s ease, background 0.12s ease, color 0.12s ease; }
    .web-chrome-links a::after { content: ' ↗'; color: #afafaf; }
    .web-chrome-links a:hover { border-color: #3989eb; background: #14203a; color: #5da7ff; }
    .web-chrome-links a:hover::after { color: #5da7ff; }
    .web-chrome-back { position: fixed; left: 43px; top: 35px; z-index: 100;
      padding: 10px 18px; border: 1px solid #2c3a4d; border-radius: 8px; background: #0d1626;
      color: #e8e8e8; font-size: 15px; font-family: 'Poppins', system-ui, sans-serif; white-space: nowrap;
      transition: border-color 0.12s ease, background 0.12s ease, color 0.12s ease; }
    .web-chrome-back:hover { border-color: #3989eb; background: #14203a; color: #5da7ff; }
    @media screen and (max-width: 950px) {
      .web-chrome-back { left: 16px; top: 16px; }
    }
    .web-chrome-observer { display: inline-block; margin-left: 10px; padding: 2px 8px; border-radius: 10px;
      font-size: 12px; background: rgba(255,255,255,0.12); color: #cfd2d6; vertical-align: middle; }
    @media (prefers-color-scheme: light) {
      .web-chrome-links a, .web-chrome-links a * { color: #181818; }
      .web-chrome-links a { border-color: #c7c9cf; background: #ffffff; }
      .web-chrome-links a:hover { color: #1f75de; background: #ffffff; border-color: #1f75de; }
      .web-chrome-links a:hover * { color: #1f75de; }
      .web-chrome-back { color: #181818; border-color: #c7c9cf; background: #ffffff; }
      .web-chrome-back:hover { color: #1f75de; background: #ffffff; border-color: #1f75de; }
      .web-chrome-observer { background: rgba(0,0,0,0.08); color: #5a5d63; }
    }
    body.web.cursor-active, body.web.cursor-active * { cursor: auto; }
    /* a * too: cursor:auto over non-text link content (logo svg) shows the arrow */
    body.web.cursor-active a, body.web.cursor-active a *, body.web.cursor-active button,
    body.web.cursor-active input, body.web.cursor-active label { cursor: pointer; }

    /* Pairing fireworks: armed the moment bootstrap flips the circle to
       load-complete (the checkmark moment), so no shared-code hook is needed. */
    .fw-layer { position: fixed; inset: 0; overflow: hidden; pointer-events: none; }
    .fw-burst { position: absolute; color: var(--c); }
    .fw-burst.fw-neutral { --c: #e8e8e8; }
    .fw-burst div {
      position: absolute; width: 6px; height: 6px; border-radius: 50%;
      background: currentColor;
      box-shadow: 0 0 10px currentColor, 0 0 20px currentColor;
      opacity: 0;
    }
    #device-finishing-container:has(.circle-loader.load-complete) .fw-burst div {
      animation: fw-shot 1.85s ease-out var(--d);
    }
    .fw-burst div:nth-child(1) { --fx: var(--r); --fy: 0px; }
    .fw-burst div:nth-child(2) { --fx: calc(var(--r) * 0.71); --fy: calc(var(--r) * 0.71); }
    .fw-burst div:nth-child(3) { --fx: 0px; --fy: var(--r); }
    .fw-burst div:nth-child(4) { --fx: calc(var(--r) * -0.71); --fy: calc(var(--r) * 0.71); }
    .fw-burst div:nth-child(5) { --fx: calc(var(--r) * -1); --fy: 0px; }
    .fw-burst div:nth-child(6) { --fx: calc(var(--r) * -0.71); --fy: calc(var(--r) * -0.71); }
    .fw-burst div:nth-child(7) { --fx: 0px; --fy: calc(var(--r) * -1); }
    .fw-burst div:nth-child(8) { --fx: calc(var(--r) * 0.71); --fy: calc(var(--r) * -0.71); }
    @keyframes fw-shot {
      0% { transform: translate(0, 0) scale(1); opacity: 1; }
      100% { transform: translate(var(--fx), var(--fy)) scale(0.2); opacity: 0; }
    }
    body.web .device-finishing .circle-loader.load-complete { animation: fw-pop 0.45s ease; }
    @keyframes fw-pop {
      40% { transform: scale(1.25); }
    }
    @media (prefers-color-scheme: light) {
      .fw-neutral { --c: #5a5d63; }
    }
    @media (prefers-reduced-motion: reduce) {
      .fw-layer { display: none; }
      body.web .device-finishing .circle-loader.load-complete { animation: none; }
    }
  `;
  document.head.appendChild(style);
}

function injectLinks(containerSelector: string): void {
  const container = document.querySelector(containerSelector);
  if (!container) return;
  const links: Array<[label: string, href: string]> = [
    [t('dashboard_link'), 'http://app.bsgn.cc/'],
    ['bohemicasignage.com', 'https://www.bohemicasignage.com/'],
  ];
  const row = document.createElement('div');
  row.className = 'web-chrome-links';
  for (const [label, href] of links) {
    const a = document.createElement('a');
    a.href = href;
    a.target = '_blank';
    a.rel = 'noopener';
    a.textContent = label;
    row.appendChild(a);
  }
  container.appendChild(row);
}

// Back to the instance picker: by pairing time the current instance is in the
// known-instances registry, so the bare URL always lands on the picker.
function injectBackButton(): void {
  const container = document.querySelector('#device-id-container');
  if (!container) return;
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'web-chrome-back';
  btn.textContent = `← ${t('back_button')}`;
  btn.addEventListener('click', navigateToPicker);
  container.appendChild(btn);
}

function injectObserverBadge(): void {
  // Peek the URL (side-effect-free) — never resolveInstanceId() here: it runs
  // before the instance picker and would write an id, skipping the picker.
  const urlId = peekUrlInstanceId();
  if (!urlId || !isObserverInstance(urlId)) return;
  const container = document.querySelector('#device-id-container');
  if (!container) return;
  const badge = document.createElement('span');
  badge.className = 'web-chrome-observer';
  badge.textContent = t('observer_badge');
  container.appendChild(badge);
}

const FIREWORK_NEUTRAL = '#e8e8e8';

// [left %, top %, color, radius px, delay s] — "grand finale": simultaneous
// pairs in the stagger, whole show ~3.2s from the checkmark (fits the ~4s
// window before content takeover, see main.ts startUpDelay).
const FIREWORK_BURSTS: Array<[number, number, string, number, number]> = [
  [15, 22, '#33b1ff', 85, 0],
  [80, 28, '#ffd166', 80, 0],
  [48, 15, '#ff7eb6', 70, 0.12],
  [25, 65, '#6ac258', 80, 0.24],
  [90, 60, FIREWORK_NEUTRAL, 65, 0.24],
  [60, 78, '#33b1ff', 75, 0.36],
  [8, 45, '#ffd166', 70, 0.48],
  [68, 10, '#6ac258', 65, 0.48],
  [38, 50, '#ff7eb6', 95, 0.6],
  [85, 85, '#b86bff', 70, 0.72],
  [18, 88, '#33b1ff', 65, 0.84],
  [55, 35, '#ffd166', 80, 0.96],
  [35, 30, '#b86bff', 75, 1.08],
  [75, 45, '#33b1ff', 80, 1.08],
  [5, 15, '#6ac258', 70, 1.2],
  [45, 88, '#ffd166', 75, 1.2],
  [95, 30, '#ff7eb6', 70, 1.32],
  [30, 8, FIREWORK_NEUTRAL, 75, 1.32],
];

function injectPairingFireworks(): void {
  const container = document.querySelector('#device-finishing-container');
  if (!container) return;
  const layer = document.createElement('div');
  layer.className = 'fw-layer';
  for (const [left, top, color, radius, delay] of FIREWORK_BURSTS) {
    const burst = document.createElement('div');
    // neutral's --c comes from the stylesheet, not inline — an inline value
    // would outrank the light-scheme override (white-on-white otherwise)
    burst.className = color === FIREWORK_NEUTRAL ? 'fw-burst fw-neutral' : 'fw-burst';
    burst.style.left = `${left}%`;
    burst.style.top = `${top}%`;
    if (color !== FIREWORK_NEUTRAL) burst.style.setProperty('--c', color);
    burst.style.setProperty('--r', `${radius}px`);
    burst.style.setProperty('--d', `${delay}s`);
    for (let i = 0; i < 8; i++) burst.appendChild(document.createElement('div'));
    layer.appendChild(burst);
  }
  container.appendChild(layer);
}

function installCursorIdle(): void {
  let idleTimer: number | null = null;
  const activity = () => {
    document.body.classList.add('cursor-active');
    if (idleTimer !== null) window.clearTimeout(idleTimer);
    idleTimer = window.setTimeout(() => {
      document.body.classList.remove('cursor-active');
      idleTimer = null;
    }, IDLE_MS);
  };
  window.addEventListener('mousemove', activity, { passive: true });
  // Touch screens never move a mouse — a tap reveals the chrome instead.
  window.addEventListener('pointerdown', activity, { passive: true });
}
